import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { Navbar } from './components/Navbar';
import Footer from './components/Footer';
import { LandingPage } from './pages/LandingPage';
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import Dashboard from './pages/Dashboard';
import { CompanyList } from './pages/company/CompanyList';
import { CompanyForm } from './components/companies/CompanyForm';
import CompanyDetail from './pages/company/CompanyDetail';
import { PrivateRoute } from './components/auth/PrivateRoute';
import { AppAdminOnly } from './components/auth/AppAdminOnly';
import { TaxRegistration } from './pages/tax';
import CompanyTaxCode from './pages/company/CompanyTaxCode';
import TaxFilingManager from './pages/tax/TaxFilingManager';
import PlatformTransactions from './pages/platforms/PlatformTransactions';
import PlatformIntegrations from './pages/platforms/PlatformIntegrations';
import ShopifyIntegration from './pages/platforms/ShopifyIntegration';
import QuickBooksIntegration from './pages/platforms/QuickBooksIntegration';
import QuickBooksCallback from './pages/QuickBooksCallback';
import CompanyUserList from './pages/users/CompanyUserList';
import TaxCodes from './pages/tax/TaxCodes';
import AdvisorPage from './pages/advisor/AdvisorPage';

import TaxCalculator from './pages/tax/TaxCalculator';
import TaxCalculationDetail from './pages/tax/TaxCalculationDetail';
import TaxCalculatorPage from './pages/TaxCalculatorPage';
import TransactionInsights from './pages/tax/TransactionInsights';
import TransactionInsightsComponent from './components/TransactionInsights';
import TaxuallyDashboard from './components/TaxuallyDashboard';
import MapNexusInsights from './components/MapNexusInsights';
import NexusRules from './pages/nexus/NexusRules';
import CompleteTaxView from './components/CompleteTaxView';
import Reports from './pages/reports/Reports';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import BlogsKnowledge from './pages/resources/BlogsKnowledge';
import TaxComplianceBlog from './pages/blog/TaxComplianceBlog';
import StateNexusGuide from './pages/resources/StateNexusGuide';
import ResourceHub from './pages/resources/ResourceHub';
import SalesTaxFAQ from './pages/SalesTaxFAQ';
import UserProfile from './pages/UserProfile';
import Settings from './pages/settings/Settings';
import Contact from './pages/contact';
import ShopifyCallback from './pages/ShopifyCallback';
import CookiePolicy from './pages/legal/CookiePolicy';
import GDPRRights from './pages/legal/GDPRRights';
import CookieConsent from './components/legal/CookieConsent';
import NotFound from './pages/NotFound';
import DevelopersPage from './pages/developers/DevelopersPage';
import DeveloperConsole from './pages/developers/DeveloperConsole';
import BillingPage from './pages/billing/BillingPage';
import { trackPageView } from './utils/analytics';

// Internal/app-admin screens load as separate chunks, never in the main bundle.
const StripeIntegration = React.lazy(() => import('./pages/platforms/StripeIntegration'));
const TransactionTaxTesting = React.lazy(() => import('./pages/tax/TransactionTaxTesting'));
const TaxApiDemo = React.lazy(() => import('./pages/tax/TaxApiDemo'));
const TaxTestingResults = React.lazy(() => import('./pages/tax/TaxTestingResults'));
const TestResultsDashboard = React.lazy(() => import('./components/TestResultsDashboard'));
const TaxEngineAdmin = React.lazy(() => import('./pages/admin/TaxEngineAdmin'));
const AdminRuleUpdates = React.lazy(() => import('./pages/admin/AdminRuleUpdates'));

const CheckoutWidgetPage = React.lazy(() => import('./pages/developers/CheckoutWidgetPage'));
const AssistantIntegrationPage = React.lazy(() => import('./pages/integrations/AssistantIntegrationPage'));
const TaxAnswerPage = React.lazy(() => import('./pages/salesTax/TaxAnswerPage'));
const NewsletterPage = React.lazy(() => import('./pages/newsletter/NewsletterPage'));
const CertificateManagement = React.lazy(() => import('./pages/exemption/CertificateManagement'));
const CustomersPage = React.lazy(() => import('./pages/customers/CustomersPage'));
const ProductsPage = React.lazy(() => import('./pages/products/ProductsPage'));
const TrustPage = React.lazy(() => import('./pages/trust/TrustPage'));
const AdminNewsletter = React.lazy(() => import('./pages/admin/AdminNewsletter'));
const AdvisorWidget = React.lazy(() => import('./components/advisor/AdvisorWidget'));

const hasAnalyticsConsent = (): boolean => {
    try {
        const stored = localStorage.getItem('cookie-preferences');
        if (!stored) return false;
        const parsed = JSON.parse(stored);
        return Boolean(parsed?.analytics);
    } catch {
        return false;
    }
};

const GetStartedRedirect: React.FC = () => {
    const { user } = useAuth();
    return <Navigate to={user ? '/onboarding' : '/register'} replace />;
};

const AppRoutes: React.FC = () => {
    const { user: authenticatedUser } = useAuth();
    const location = useLocation();

    useEffect(() => {
        if (!hasAnalyticsConsent()) return;
        const path = `${location.pathname}${location.search}${location.hash}`;
        trackPageView(path);
    }, [location.pathname, location.search, location.hash]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navbar />
            <Box sx={{ flexGrow: 1, pt: 8 }}>
                <Suspense fallback={<Box sx={{ minHeight: "60vh" }} />}>
                <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/advisor" element={<Navigate to="/products/advisor" replace />} />
                    <Route path="/products/advisor" element={<AdvisorPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/quickbooks/callback" element={<QuickBooksCallback />} />
                    <Route path="/get-started" element={<GetStartedRedirect />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/shopify/callback" element={<ShopifyCallback />} />
                    <Route path="/tos" element={<TermsOfServicePage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/gdpr-rights" element={<GDPRRights />} />
                    
                    {/* SEO-optimized content pages */}
                    <Route path="/blog/sales-tax-compliance-guide-2025" element={<TaxComplianceBlog />} />
                    <Route path="/resources/state-nexus-thresholds" element={<StateNexusGuide />} />
                    <Route path="/resources" element={<ResourceHub />} />
                    <Route path="/faq" element={<SalesTaxFAQ />} />
                    <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
                    <Route path="/tax/nexus-threshold" element={<Navigate to="/nexus/dashboard" replace />} />
                    <Route path="/integrations/shopify" element={<Navigate to="/platforms/shopify-integration" replace />} />
                    <Route path="/integrations/quickbooks" element={<Navigate to="/platforms/integrations" replace />} />
                    <Route path="/integrations/stripe" element={<Navigate to="/platforms/integrations" replace />} />

                    {/* Developer API: public docs/marketing page and the signed-in key console */}
                    <Route path="/developers" element={<DevelopersPage />} />
                    <Route path="/developers/checkout" element={<CheckoutWidgetPage />} />
                    <Route path="/integrations/grok" element={<AssistantIntegrationPage kind="grok" />} />
                    <Route path="/integrations/chatgpt" element={<AssistantIntegrationPage kind="chatgpt" />} />
                    <Route path="/sales-tax" element={<TaxAnswerPage />} />
                    <Route path="/sales-tax/:slug" element={<TaxAnswerPage />} />
                    <Route path="/trust" element={<TrustPage />} />
                    <Route path="/accuracy" element={<Navigate to="/trust" replace />} />
                    <Route path="/newsletter" element={<NewsletterPage />} />
                    <Route path="/newsletter/:slug" element={<NewsletterPage />} />
                    <Route path="/exemption/submit/:token" element={<CertificateManagement buyerMode />} />
                    <Route
                        path="/developers/console"
                        element={
                            <PrivateRoute>
                                <DeveloperConsole />
                            </PrivateRoute>
                        }
                    />

                    {/* App-admin only (the API enforces get_current_active_app_admin) */}
                    <Route
                        path="/admin/tax-engine"
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <TaxEngineAdmin />
                                </AppAdminOnly>
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/admin/newsletter"
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <AdminNewsletter />
                                </AppAdminOnly>
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/admin/rule-updates"
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <AdminRuleUpdates />
                                </AppAdminOnly>
                            </PrivateRoute>
                        }
                    />

                    {/* Protected routes */}
                    <Route path="/exemptions" element={<PrivateRoute><CertificateManagement /></PrivateRoute>} />
                    <Route path="/customers" element={<PrivateRoute><CustomersPage /></PrivateRoute>} />
                    <Route path="/products" element={<PrivateRoute><ProductsPage /></PrivateRoute>} />
                    <Route
                        path="/dashboard" 
                        element={
                            <PrivateRoute>
                                <Dashboard />
                            </PrivateRoute>
                        } 
                    />

                    <Route
                        path="/onboarding"
                        element={
                            <PrivateRoute>
                                <OnboardingPage />
                            </PrivateRoute>
                        }
                    />

                    <Route 
                        path="/complete-tax-view" 
                        element={
                            <PrivateRoute>
                                <CompleteTaxView />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/tax/complete" 
                        element={
                            <PrivateRoute>
                                <CompleteTaxView />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/companies" 
                        element={
                            <PrivateRoute>
                                <CompanyList />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/company" 
                        element={<Navigate to="/companies" replace />} 
                    />

                    <Route 
                        path="/companies/new" 
                        element={
                            <PrivateRoute>
                                <CompanyForm mode="create" />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/companies/:id" 
                        element={
                            <PrivateRoute>
                                <CompanyDetail />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/companies/:id/edit" 
                        element={
                            <PrivateRoute>
                                <CompanyForm mode="edit" />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/company/:companyId/tax-codes" 
                        element={
                            <PrivateRoute>
                                <CompanyTaxCode />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/company/users" 
                        element={
                            <PrivateRoute>
                                <CompanyUserList />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/profile" 
                        element={
                            <PrivateRoute>
                                <UserProfile />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/settings" 
                        element={
                            <PrivateRoute>
                                <Settings />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/reports" 
                        element={
                            <PrivateRoute>
                                <Reports />
                            </PrivateRoute>
                        } 
                    />

                    {/* Tax-related routes */}
                    <Route 
                        path="/tax/registration" 
                        element={
                            <PrivateRoute>
                                <TaxRegistration />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/tax/calculator" 
                        element={
                            <PrivateRoute>
                                <TaxCalculator />
                            </PrivateRoute>
                        } 
                    />

                    <Route path="/tax/advisor" element={<Navigate to="/products/advisor" replace />} />

                    <Route 
                        path="/tax/calculation/:id" 
                        element={
                            <PrivateRoute>
                                <TaxCalculationDetail />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/tax-calculator" 
                        element={
                            <PrivateRoute>
                                <TaxCalculatorPage />
                            </PrivateRoute>
                        } 
                    />

                    <Route
                        path="/billing"
                        element={
                            <PrivateRoute>
                                <BillingPage />
                            </PrivateRoute>
                        }
                    />

                    <Route 
                        path="/tax/filings" 
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <TaxFilingManager />
                                </AppAdminOnly>
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/tax/codes" 
                        element={
                            <PrivateRoute>
                                <TaxCodes />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/tax/insights" 
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <TransactionInsights />
                                </AppAdminOnly>
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/tax/insights/testing" 
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <TransactionTaxTesting />
                                </AppAdminOnly>
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/tax/insights/demo" 
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <TaxApiDemo />
                                </AppAdminOnly>
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/tax/testing/results" 
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <TaxTestingResults />
                                </AppAdminOnly>
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/test-results" 
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <TestResultsDashboard />
                                </AppAdminOnly>
                            </PrivateRoute>
                        } 
                    />

                    {/* Platform integration routes */}
                    <Route 
                        path="/platforms/integrations" 
                        element={
                            <PrivateRoute>
                                <PlatformIntegrations />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/platforms" 
                        element={
                            <PrivateRoute>
                                <PlatformIntegrations />
                            </PrivateRoute>
                        } 
                    />

                    {/* Bare /platforms/transactions has no integration to load (was GET /integrations/undefined -> 422);
                        the all-integrations transaction list lives in the Complete Tax View. */}
                    <Route path="/platforms/transactions" element={<Navigate to="/complete-tax-view" replace />} />
                    <Route 
                        path="/platforms/transactions/:integrationId" 
                        element={
                            <PrivateRoute>
                                <PlatformTransactions />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/platforms/shopify-integration" 
                        element={
                            <PrivateRoute>
                                <ShopifyIntegration />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/platforms/reference-engine-testing" 
                        element={
                            <PrivateRoute>
                                <AppAdminOnly>
                                    <StripeIntegration />
                                </AppAdminOnly>
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/platforms/quickbooks-integration" 
                        element={
                            <PrivateRoute>
                                <QuickBooksIntegration />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/taxually" 
                        element={
                            <PrivateRoute>
                                <TaxuallyDashboard />
                            </PrivateRoute>
                        } 
                    />

                    {/* Nexus routes */}
                    <Route path="/nexus" element={<Navigate to="/nexus/dashboard" replace />} />
                    
                    <Route 
                        path="/nexus/dashboard" 
                        element={
                            <PrivateRoute>
                                <MapNexusInsights />
                            </PrivateRoute>
                        } 
                    />

                    {/* Company-scoped Nexus dashboard (deep link + CompanySelector navigation) */}
                    <Route 
                        path="/company/:companyId/nexus/dashboard" 
                        element={
                            <PrivateRoute>
                                <MapNexusInsights />
                            </PrivateRoute>
                        } 
                    />

                    <Route 
                        path="/nexus/rules" 
                        element={
                            <PrivateRoute>
                                <NexusRules />
                            </PrivateRoute>
                        } 
                    />

                    {/* Company-scoped Transaction Insights */}
                    <Route 
                        path="/company/:companyId/transaction-insights" 
                        element={
                            <PrivateRoute>
                                {/* @ts-ignore - companyId is read from URL params via useParams() */}
                                <TransactionInsightsComponent />
                            </PrivateRoute>
                        } 
                    />

                    {/* Other routes */}
                    <Route 
                        path="/resources/blogs-knowledge" 
                        element={<BlogsKnowledge />}
                    />

                    <Route path="/about/contact" element={<Contact />} />
                    <Route path="/about/company" element={<Navigate to="/" />} />
                    
                    {/* Product menu redirects */}
                    <Route path="/products/calculator" element={<Navigate to="/tax/calculator" />} />
                    <Route path="/products/registration" element={<Navigate to="/tax/registration" />} />
                    <Route path="/products/filings" element={<Navigate to="/tax/filings" />} />
                    <Route path="/products/test-results" element={<Navigate to="/test-results" />} />
                    
                    {/* Catch-all */}
                    <Route path="*" element={<NotFound />} />
                    
                </Routes>
                </Suspense>
            </Box>
            <Footer />
            <Suspense fallback={null}><AdvisorWidget /></Suspense>
            <CookieConsent />
        </Box>
    );
};

export default AppRoutes; 
